/** @typedef {'yearly' | 'monthly' | 'onetime'} BillingMode */
/** @typedef {'starter' | 'pro' | 'ultra' | 'small' | 'medium' | 'large' | 'ultra_ot'} PlanTier */

export const BILLING_MODES = {
  yearly: 'yearly',
  monthly: 'monthly',
  onetime: 'onetime',
}

export const PRICING_AVAILABLE_BILLING_MODES = [
  BILLING_MODES.yearly,
  BILLING_MODES.monthly,
  BILLING_MODES.onetime,
]

export const DEFAULT_BILLING_MODE = BILLING_MODES.yearly

export function isPricingBillingModeAvailable(billing) {
  return PRICING_AVAILABLE_BILLING_MODES.includes(billing)
}

export function normalizePricingBillingMode(billing) {
  return isPricingBillingModeAvailable(billing) ? billing : DEFAULT_BILLING_MODE
}

/** 一次性最小档 SKU */
export const SMALL_ONE_TIME_SKU_KEY = 'small_one_time'
// export const PRICING_SMALL_ONETIME_DISCOUNT_CODE = 'LPARP25OF'

const ONETIME_SKUS = [
  { key: 'ultra_one_time', tier: 'ultra_ot', credits: 1900, priceUsd: 64.9, discountPct: 35, originalPriceUsd: 100, recommended: false },
  { key: 'large_one_time', tier: 'large', credits: 1160, priceUsd: 39.9, discountPct: 34, originalPriceUsd: 60, recommended: false },
  { key: 'medium_one_time', tier: 'medium', credits: 750, priceUsd: 25.9, discountPct: 33, originalPriceUsd: 37, recommended: true },
  { key: 'small_one_time', tier: 'small', credits: 255, priceUsd: 8.9, discountPct: 32, originalPriceUsd: 13, recommended: false },
]

const PRO_SLIDER_STEPS = [
  {
    creditsMonthly: 595,
    creditsYearly: 440,
    priceUsdMonthly: 19.99,
    priceUsdYearly: 167.88,
    discountPctMonthly: 33,
    discountPctYearly: 53,
    originalMonthlyUsdMonthly: 30,
    originalMonthlyUsdYearly: 20,
  },
  {
    creditsMonthly: 1205,
    creditsYearly: 890,
    priceUsdMonthly: 39.99,
    priceUsdYearly: 335.88,
    discountPctMonthly: 43,
    discountPctYearly: 54,
    originalMonthlyUsdMonthly: 70,
    originalMonthlyUsdYearly: 60,
  },
  {
    creditsMonthly: 2130,
    creditsYearly: 1570,
    priceUsdMonthly: 69.99,
    priceUsdYearly: 587.88,
    discountPctMonthly: 46,
    discountPctYearly: 55,
    originalMonthlyUsdMonthly: 130,
    originalMonthlyUsdYearly: 110,
  },
]

const ULTRA_SLIDER_STEPS = [
  {
    creditsMonthly: 3060,
    creditsYearly: 2260,
    priceUsdMonthly: 99.99,
    priceUsdYearly: 839.88,
    discountPctMonthly: 50,
    discountPctYearly: 56,
    originalMonthlyUsdMonthly: 200,
    originalMonthlyUsdYearly: 160,
  },
  {
    creditsMonthly: 5820,
    creditsYearly: 4535,
    priceUsdMonthly: 188.99,
    priceUsdYearly: 1679.88,
    discountPctMonthly: 58,
    discountPctYearly: 59,
    originalMonthlyUsdMonthly: 450,
    originalMonthlyUsdYearly: 340,
  },
  {
    creditsMonthly: 12060,
    creditsYearly: 9425,
    priceUsdMonthly: 388.99,
    priceUsdYearly: 3479.88,
    discountPctMonthly: 60,
    discountPctYearly: 62,
    originalMonthlyUsdMonthly: 970,
    originalMonthlyUsdYearly: 760,
  },
]

const STARTER_SKUS = {
  monthly: { credits: 205, priceUsd: 6.99, discountPct: 30, originalMonthlyUsd: 10 },
  yearly: { credits: 155, priceUsd: 59.88, discountPct: 50, originalMonthlyUsd: 10 },
}

export const DEFAULT_SLIDER_INDEX = 1

export const CONCURRENT_TASKS = {
  onetime: 3,
  monthly: 4,
  yearly: 5,
}

export const YEARLY_TAB_DISCOUNT = 62

/** Shared model names for feature list (logos rendered in UI). */
export const PRICING_MODEL_ICON_CSS = {
  micIcon: 'lip-svg-micIcon',
  animalsCatIcon: 'lip-svg-animalsCatIcon',
  babyFaceIcon: 'lip-svg-babyFaceIcon',
  influencerIcon: 'lip-svg-influencerIcon',
  talkingPhotoIcon: 'lip-svg-talkingPhotoIcon',
  talkingChatIcon: 'lip-svg-talkingChatIcon',
}

/** 定价卡片模型区：侧栏图标用更饱和的主题色 */
export const PRICING_MODEL_ICON_COLORS = {
  micIcon: 'text-sky-500 dark:text-sky-400',
  animalsCatIcon: 'text-blue-500 dark:text-blue-400',
  babyFaceIcon: 'text-rose-500 dark:text-rose-400',
  influencerIcon: 'text-violet-500 dark:text-violet-400',
  talkingPhotoIcon: 'text-indigo-500 dark:text-indigo-400',
  talkingChatIcon: 'text-cyan-500 dark:text-cyan-400',
}

export const PRICING_MODEL_FEATURES = [
  { id: 'lip-sync-1', label: 'Lip Sync 1.0', icon: 'lipsync' },
  { id: 'lip-sync-2', label: 'Lip Sync 2.0', icon: 'lipsync' },
  { id: 'talking-1', label: 'Talking 1.0', icon: 'micIcon' },
  { id: 'talking-2', label: 'Talking 2.0', icon: 'micIcon' },
  { id: 'talking-3', label: 'Talking 3.0', icon: 'micIcon' },
  { id: 'talking-4', label: 'Talking 4.0', icon: 'micIcon' },
  { id: 'talking-animals-1', label: 'Talking Animals 1.0', icon: 'animalsCatIcon' },
  { id: 'talking-animals-2', label: 'Talking Animals 2.0', icon: 'animalsCatIcon' },
  { id: 'baby-1', label: 'Talking baby 1.0', icon: 'babyFaceIcon' },
  { id: 'baby-2', label: 'Talking baby 2.0', icon: 'babyFaceIcon' },
  { id: 'influencer-1', label: 'Influencer 1.0', icon: 'influencerIcon' },
  { id: 'influencer-2', label: 'Influencer 2.0', icon: 'influencerIcon' },
  { id: 'influencer-3', label: 'Influencer 3.0', icon: 'influencerIcon' },
  { id: 'influencer-4', label: 'Influencer 4.0', icon: 'influencerIcon' },
  { id: 'seedance', label: 'Seedance 1.5 Pro', seedance: true },
  { id: 'seedance-2', label: 'Seedance 2.0', seedance: true },
  { id: 'dialogue-1', label: 'Dialogue 1.0', icon: 'talkingChatIcon' },
  { id: 'dialogue-2', label: 'Dialogue 2.0', icon: 'talkingChatIcon' },
  { id: 'dialogue-3', label: 'Dialogue 3.0', icon: 'talkingChatIcon' },
]

/** Models shown in pricing card grid (matches design mock). */
export const PRICING_CARD_MODEL_IDS = [
  'lip-sync-1',
  'lip-sync-2',
  'talking-1',
  'talking-2',
  'talking-3',
  'talking-4',
  'talking-animals-1',
  'talking-animals-2',
  'baby-1',
  'baby-2',
  'influencer-1',
  'influencer-2',
  'influencer-3',
  'influencer-4',
  'seedance',
  'seedance-2',
  'dialogue-1',
  'dialogue-2',
  'dialogue-3',
]

export function getPricingModelIconCss(model) {
  if (!model?.icon || model.icon === 'lipsync' || model.seedance) return null
  return PRICING_MODEL_ICON_CSS[model.icon] ?? null
}

export function getPricingModelIconColorClass(model) {
  if (!model?.icon || model.icon === 'lipsync' || model.seedance) return null
  return PRICING_MODEL_ICON_COLORS[model.icon] ?? 'text-primary-600 dark:text-primary-400'
}

export function getPricingCardModels() {
  const byId = Object.fromEntries(PRICING_MODEL_FEATURES.map((m) => [m.id, m]))
  return PRICING_CARD_MODEL_IDS.map((id) => byId[id]).filter(Boolean)
}

export function getPriceCode(skuKey, productIds = {}) {
  return productIds[skuKey] ?? ''
}

export function formatPriceUsd(value) {
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** 年付小字：$1,679.88/year No auto-renewal（见 docs/pricing 年付备注） */
export function formatYearlyBillingNote(t, priceUsd) {
  return t('price.billedAnnuallyNote', {
    price: `$${formatPriceUsd(priceUsd)}`,
  })
}

export function creditsPerDollar(credits, priceUsd, billing) {
  if (!priceUsd) return 0
  const monthlyEquivalent = billing === BILLING_MODES.yearly ? priceUsd / 12 : priceUsd
  return Math.round((credits / monthlyEquivalent) * 10) / 10
}

export function videoSecondsEstimate(credits) {
  return credits
}

export function originalPriceFromDiscount(currentPrice, discountPct) {
  if (!discountPct || discountPct >= 100) return currentPrice
  return Math.round((currentPrice / (1 - discountPct / 100)) * 100) / 100
}

function resolveOriginalMonthly(displayMonthly, discountPct, explicitOriginal) {
  if (explicitOriginal != null) return explicitOriginal
  return originalPriceFromDiscount(displayMonthly, discountPct)
}

function skuKeyForSubscription(tier, billing, sliderIndex) {
  if (tier === 'starter') return `starter_${billing}`
  const size = ['small', 'medium', 'large'][sliderIndex] ?? 'medium'
  return `${tier}_${size}_${billing}`
}

function resolveSliderCredits(step, billing) {
  return billing === BILLING_MODES.yearly ? step.creditsYearly : step.creditsMonthly
}

function buildSubscriptionCard(tier, billing, sliderSteps, sliderIndex, productIds, options = {}) {
  const isStarter = tier === 'starter'
  const step = isStarter ? null : sliderSteps[sliderIndex] ?? sliderSteps[DEFAULT_SLIDER_INDEX]
  const credits = isStarter ? STARTER_SKUS[billing].credits : resolveSliderCredits(step, billing)
  const priceUsd = isStarter
    ? STARTER_SKUS[billing].priceUsd
    : billing === BILLING_MODES.yearly
      ? step.priceUsdYearly
      : step.priceUsdMonthly
  const discountPct = isStarter
    ? STARTER_SKUS[billing].discountPct
    : billing === BILLING_MODES.yearly
      ? step.discountPctYearly
      : step.discountPctMonthly
  const key = skuKeyForSubscription(tier, billing, sliderIndex)
  const displayMonthly = billing === BILLING_MODES.yearly ? priceUsd / 12 : priceUsd
  const explicitOriginalMonthly = isStarter
    ? STARTER_SKUS[billing].originalMonthlyUsd
    : billing === BILLING_MODES.yearly
      ? step.originalMonthlyUsdYearly
      : step.originalMonthlyUsdMonthly

  return {
    key,
    tier,
    billing,
    credits,
    priceUsd,
    displayMonthly,
    discountPct,
    recommended: tier === 'pro',
    hasSlider: !isStarter,
    sliderIndex: isStarter ? null : sliderIndex,
    price_code: getPriceCode(key, productIds),
    originalMonthly: resolveOriginalMonthly(displayMonthly, discountPct, explicitOriginalMonthly),
    creditsPerDollar: creditsPerDollar(credits, priceUsd, billing),
    videoSeconds: videoSecondsEstimate(credits),
    ...options,
  }
}

function buildOnetimeCard(sku, productIds) {
  return {
    key: sku.key,
    tier: sku.tier,
    billing: BILLING_MODES.onetime,
    credits: sku.credits,
    priceUsd: sku.priceUsd,
    displayMonthly: null,
    discountPct: sku.discountPct,
    recommended: sku.recommended,
    hasSlider: false,
    price_code: getPriceCode(sku.key, productIds),
    originalPrice: sku.originalPriceUsd ?? originalPriceFromDiscount(sku.priceUsd, sku.discountPct),
    creditsPerDollar: Math.round((sku.credits / sku.priceUsd) * 10) / 10,
    videoSeconds: videoSecondsEstimate(sku.credits),
  }
}

/** Cards for current billing mode (desktop grid order). */
export function getPricingCards(billing, sliderState = { pro: DEFAULT_SLIDER_INDEX, ultra: DEFAULT_SLIDER_INDEX }, productIds = {}) {
  const normalizedBilling = normalizePricingBillingMode(billing)

  if (normalizedBilling === BILLING_MODES.onetime) {
    return ONETIME_SKUS.map((sku) => buildOnetimeCard(sku, productIds))
  }

  const order = ['ultra', 'pro', 'starter']
  return order.map((tier) =>
    buildSubscriptionCard(
      tier,
      normalizedBilling,
      tier === 'pro' ? PRO_SLIDER_STEPS : ULTRA_SLIDER_STEPS,
      tier === 'starter' ? 0 : sliderState[tier] ?? DEFAULT_SLIDER_INDEX,
      productIds,
    ),
  )
}

export function getOnetimeCards(productIds = {}) {
  return ONETIME_SKUS.map((sku) => buildOnetimeCard(sku, productIds))
}

export function getSliderSteps(tier, billing = BILLING_MODES.monthly) {
  const steps = tier === 'pro' ? PRO_SLIDER_STEPS : ULTRA_SLIDER_STEPS
  return steps.map((step) => ({
    ...step,
    credits: resolveSliderCredits(step, billing),
  }))
}

const SLIDER_SIZE_TO_INDEX = { small: 0, medium: 1, large: 2 }

export function parseSliderIndexFromSkuKey(skuKey) {
  if (!skuKey) return DEFAULT_SLIDER_INDEX
  const match = String(skuKey).match(/_(small|medium|large)_/)
  if (!match) return DEFAULT_SLIDER_INDEX
  return SLIDER_SIZE_TO_INDEX[match[1]] ?? DEFAULT_SLIDER_INDEX
}

/** 从 product_id 反查 billing / tier / 滑条档位（支付回跳恢复选中套餐） */
export function resolvePlanSelectionFromProductId(productId, productIds = {}) {
  const normalizedId = String(productId || '').trim()
  if (!normalizedId) return null

  for (const sku of ONETIME_SKUS) {
    if (getPriceCode(sku.key, productIds) === normalizedId) {
      return {
        billing: BILLING_MODES.onetime,
        tier: sku.tier,
        skuKey: sku.key,
        sliderStatePatch: {},
      }
    }
  }

  for (const billing of [BILLING_MODES.monthly, BILLING_MODES.yearly]) {
    const starterKey = `starter_${billing}`
    if (getPriceCode(starterKey, productIds) === normalizedId) {
      return {
        billing,
        tier: 'starter',
        skuKey: starterKey,
        sliderStatePatch: {},
      }
    }

    for (const tier of ['pro', 'ultra']) {
      for (let sliderIndex = 0; sliderIndex < 3; sliderIndex += 1) {
        const key = skuKeyForSubscription(tier, billing, sliderIndex)
        if (getPriceCode(key, productIds) === normalizedId) {
          return {
            billing,
            tier,
            skuKey: key,
            sliderStatePatch: { [tier]: sliderIndex },
          }
        }
      }
    }
  }

  return null
}

/** Restore billing / tier / slider from pay-plan click analytics context. */
export function resolvePurchasedPlanSelection(payContext) {
  const plan = payContext?.plan ?? payContext
  if (!plan?.tier || !plan?.billing) return null
  if (!Object.values(BILLING_MODES).includes(plan.billing)) return null
  if (!isPricingBillingModeAvailable(plan.billing)) return null

  /** @type {Record<string, number>} */
  const sliderStatePatch = {}
  if (plan.tier === 'pro' || plan.tier === 'ultra') {
    const sliderIndex = plan.slider_index ?? plan.sliderIndex
    if (sliderIndex != null && Number.isFinite(Number(sliderIndex))) {
      sliderStatePatch[plan.tier] = Number(sliderIndex)
    } else {
      sliderStatePatch[plan.tier] = parseSliderIndexFromSkuKey(plan.sku_key ?? plan.skuKey)
    }
  }

  return {
    billing: plan.billing,
    tier: plan.tier,
    sliderStatePatch,
  }
}

export function resolvePlanSummary(plan, productIds = {}) {
  if (!plan) return null
  return {
    key: plan.key,
    skuKey: plan.key,
    tier: plan.tier,
    billing: plan.billing,
    credits: plan.credits,
    priceUsd: plan.priceUsd,
    displayMonthly: plan.displayMonthly,
    sliderIndex: plan.sliderIndex ?? null,
    price_code: plan.price_code || getPriceCode(plan.key, productIds),
    creditsPerDollar: plan.creditsPerDollar,
    videoSeconds: plan.videoSeconds,
  }
}
